import { formatCreditUnits } from '../model/formatBillingUnits';
import {
  Alert,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useModelCostControllerUsageQuery } from '@shared/services/apiService';

export const ResourceConsumption = ({
  companyId,
  callId,
}: {
  companyId: string;
  callId: string;
}) => {
  const query = useModelCostControllerUsageQuery(
    { companyId, callId },
    {
      skip: !companyId || !callId,
      refetchOnMountOrArgChange: true,
      pollingInterval: 15000,
      skipPollingIfUnfocused: true,
    },
  );
  const data = query.currentData;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="h6">Resource consumption</Typography>
          {data && <Chip size="small" label={data.state} />}
        </Stack>
        {query.isError && <Alert severity="error">Unable to load consumption.</Alert>}
        {query.isLoading && <Typography variant="body2">Loading consumption…</Typography>}
        {data?.reason && (
          <Typography variant="body2" color="text.secondary">
            {data.reason}
          </Typography>
        )}
        {data && (
          <>
            <TableContainer>
              <Table size="small" aria-label="Resource consumption">
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell>Usage</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((line, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        {line.provider} / {line.model || 'Unknown model'}
                      </TableCell>
                      <TableCell>
                        {line.unit === 'llm-token-in'
                          ? data.inputIncludesCached
                            ? 'Input tokens (includes cached)'
                            : line.cachedInput === undefined
                              ? 'Input tokens (cache split unknown)'
                              : 'Uncached input tokens'
                          : line.unit === 'llm-cached-tokens'
                            ? 'Cached input tokens'
                            : line.unit === 'llm-token-out'
                              ? 'Output tokens'
                              : line.unit === 'stt-second'
                                ? 'STT seconds'
                                : line.unit === 'tts-character'
                                  ? 'TTS characters'
                                  : line.unit}
                      </TableCell>
                      <TableCell align="right">
                        {line.quantity}
                        {data.inputIncludesCached &&
                          line.cachedInput !== undefined &&
                          ` (${line.cachedInput} cached)`}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!data.lines.length && (
                    <TableRow>
                      <TableCell colSpan={3}>Consumption data is not available yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {data.comparisonCreditUnits !== null && data.creditScale !== null && (
              <Typography variant="body2" color="text.secondary">
                Shadow comparison: {formatCreditUnits(data.comparisonCreditUnits, data.creditScale)}{' '}
                credits — no additional charge
              </Typography>
            )}
            {data.creditUnits !== null && data.creditScale !== null && (
              <Typography variant="body2">
                Charged: {formatCreditUnits(data.creditUnits, data.creditScale)} credits
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
